import { IonButton, IonLabel, IonNote, IonProgressBar } from '@ionic/react';
import { useEffect, useRef, useState } from 'react';
import { on, removeListener, run } from '../../helper/electron';

const START = 'track-norm-backfill/start';
const CANCEL = 'track-norm-backfill/cancel';
const STATUS = 'track-norm-backfill/status';
const PROGRESS = 'track-norm-backfill/progress';

interface BackfillProgress {
  processed: number;
  total: number;
  state: 'idle' | 'running' | 'done' | 'cancelled' | 'error';
  error?: string;
}

/**
 * Lets a user run (or stop) the track match index update themselves.
 *
 * It also runs on its own in the background after launch, so this is mostly for
 * large libraries where someone wants to defer the work, or to retry after
 * cancelling. Nothing here is required for matching to function; the index only
 * improves how accented and punctuated names are discovered.
 */
export default function TrackIndexOptimizer() {
  const [progress, setProgress] = useState<BackfillProgress | null>(null);
  const listenerRef = useRef<((...args: unknown[]) => void) | null>(null);

  useEffect(() => {
    const listener = (payload: BackfillProgress) => setProgress(payload);
    listenerRef.current = listener as unknown as (...a: unknown[]) => void;
    on(PROGRESS, listener);
    run(STATUS);

    return () => {
      if (listenerRef.current) {
        removeListener(PROGRESS, listenerRef.current);
      }
    };
  }, []);

  const running = progress?.state === 'running';
  const remaining = progress ? progress.total - progress.processed : 0;
  const ratio =
    progress && progress.total > 0 ? progress.processed / progress.total : 0;

  const describe = (): string => {
    if (!progress) return 'Checking…';
    if (running) {
      return `Updating ${progress.processed.toLocaleString()} of ${progress.total.toLocaleString()} tracks`;
    }
    if (progress.state === 'error') {
      return `Stopped: ${progress.error ?? 'unknown error'}. Progress was saved.`;
    }
    if (progress.state === 'cancelled') {
      return `Paused with ${remaining.toLocaleString()} tracks left. Resuming picks up where it stopped.`;
    }
    if (progress.total === 0) return 'Your library is fully optimized.';
    return `${progress.total.toLocaleString()} tracks are waiting to be optimized.`;
  };

  // Nothing outstanding and nothing happening: no action to offer.
  if (progress && progress.state !== 'running' && progress.total === 0) {
    return (
      <IonLabel position="stacked">
        <h3>Match index</h3>
        <p>Your library is fully optimized.</p>
      </IonLabel>
    );
  }

  return (
    <>
      <IonLabel position="stacked">
        <h3>Match index</h3>
        <p>
          Improves how accented and punctuated names are found (Beyoncé,
          V.I.C.). Runs automatically in the background; large libraries can
          take a few minutes.
        </p>
      </IonLabel>

      {running && <IonProgressBar value={ratio} />}
      <IonNote>{describe()}</IonNote>

      <IonButton
        size="small"
        fill={running ? 'outline' : 'solid'}
        onClick={() => run(running ? CANCEL : START)}
      >
        {running ? 'Pause' : 'Optimize now'}
      </IonButton>
    </>
  );
}
